import { type ReactNode, createContext, useContext } from "react"

import { cn } from "@/lib/utils"

// Optional top-right action rendered inside the wizard's scroll content (so it
// scrolls with the step rather than staying pinned). A page wraps its steps in
// this provider to inject an action; steps that aren't wrapped render nothing.
export const WizardActionContext = createContext<ReactNode>(null)

// Full-height wizard frame. The scrollable area holds the step content; the
// footer (when provided) is pinned to the bottom of the viewport and hosts
// the step's navigation buttons (Back / Continue / Finish).
export function WizardLayout({
  children,
  footer,
}: {
  children: ReactNode
  footer?: ReactNode
}) {
  const action = useContext(WizardActionContext)
  return (
    <div className="h-app-viewport flex flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {action && <div className="flex justify-end px-4 pt-4">{action}</div>}
        <div
          className={cn(
            "flex flex-col items-center px-4 pb-6 sm:px-6 lg:px-10",
            action ? "pt-6" : "pt-10 sm:pt-16",
          )}
        >
          <div className="w-full max-w-xl space-y-8">{children}</div>
        </div>
      </div>
      {footer && (
        <div className="bg-background flex shrink-0 justify-end gap-2 border-t px-4 py-3 sm:px-6 sm:py-4 lg:px-10">
          {footer}
        </div>
      )}
    </div>
  )
}

export function StepHeader({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string
  title: string
  description?: ReactNode
}) {
  return (
    <div>
      <p className="text-muted-foreground mb-4 text-xs font-medium tracking-wide uppercase">
        {eyebrow}
      </p>
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      {description && (
        <p className="text-muted-foreground text-sm leading-relaxed">
          {description}
        </p>
      )}
    </div>
  )
}
